import React, { useEffect, useRef, useState } from 'react';
import { orders as ordersApi, exchangeRate as fxApi } from '../utils/api';
import { fmtBRL } from '../utils/helpers';
import { CAT_LABELS, CATS, FOREIGN_CATS, fmtIn, parseBRL, parseRate, fmtBRLInput } from '../utils/payments';

const emptyAllocs = () => CATS.reduce((o, c) => { o[c] = { on: false, amount: '', order: '', final: false }; return o; }, {});
const emptyMeta = () => ({ exchange_rate: '', brl_total: '', rate_mode: 'rate', payment_date: '', notes: '', currency: 'BRL', payee: '' });

// Estado inicial do formulário a partir de um pagamento já gravado (edição)
function initFromPayment(p) {
  const cur = p.currency || 'BRL';
  const foreignPay = cur !== 'BRL';
  const rate = parseFloat(p.exchange_rate) || 1;
  const brlPaid = parseFloat(p.amount_brl) || 0;
  const meta = {
    currency: cur,
    exchange_rate: foreignPay ? String(rate) : '1',
    // Reabre no modo em que foi lançado: pelo R$ debitado ('brl') ou pelo
    // câmbio ('rate', inclusive pagamentos antigos). Pagamento de valor zero
    // (só "final") sempre pelo câmbio — não há R$ de onde calcular.
    brl_total: foreignPay && brlPaid > 0 ? fmtBRLInput(brlPaid) : '',
    rate_mode: foreignPay && p.entry_mode === 'brl' && brlPaid > 0 ? 'brl' : 'rate',
    payment_date: (p.payment_date || '').slice(0, 10),
    notes: p.notes || '',
    payee: p.payee || '',
  };
  const allocs = emptyAllocs();
  const list = (Array.isArray(p.allocations) && p.allocations.length)
    ? p.allocations
    : [{ category: p.category || 'fornecedor', amount_brl: p.amount_brl, is_final: 0 }];
  list.forEach(a => {
    if (!allocs[a.category]) return;
    const brl = a.amount_brl || 0;
    allocs[a.category] = {
      on: true,
      // guardamos em BRL; no formulário fornecedor/frete ficam na moeda do
      // pagamento e as categorias em reais, em R$
      amount: (foreignPay && !FOREIGN_CATS.includes(a.category) ? brl : brl / (rate || 1)).toFixed(2),
      // vírgula decimal: "12,345" volta 12.345 (com ponto seria lido como milhar)
      order: a.amount_order > 0 ? String(a.amount_order).replace('.', ',') : '',
      final: !!a.is_final,
    };
  });
  return { meta, allocs };
}

// Modal de registrar / editar pagamento do pedido. `editing` = pagamento em
// edição (null para um novo). Cada abertura monta o formulário do zero.
export default function PaymentForm({ quotation: q, editing, onClose, onSaved, toast }) {
  const balance = q.balance || null;
  const [init] = useState(() => (editing ? initFromPayment(editing) : { meta: emptyMeta(), allocs: emptyAllocs() }));
  const [meta, setMeta] = useState(init.meta);
  const [allocs, setAllocs] = useState(init.allocs);
  const [receipt, setReceipt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removeReceipt, setRemoveReceipt] = useState(false);

  const rows = balance?.rows || [];
  const rowOf = (cat) => rows.find(r => r.category === cat);
  const saldoOf = (cat) => rowOf(cat)?.balance ?? 0;
  const brlNative = meta.currency === 'BRL';
  const orderCur = balance?.currency || 'USD';
  const rmbRate = balance?.rmb_rate || q.rmb_rate || 6.7;
  // Taxa RMB/USD deste pagamento: na edição, a congelada no pagamento (é a que o
  // backend usa para abater a dívida); em pagamento novo, a atual do pedido.
  const rmbFor = (cur) => (editing && editing.currency === cur && editing.rmb_rate_at_payment) || rmbRate;
  const payRmbRate = rmbFor(meta.currency);
  // Busca de câmbio em andamento: respostas de uma busca antiga são descartadas
  const fxReq = useRef(0);

  // Fornecedor e frete: a dívida é fixa na moeda do pedido (USD/RMB) e vem
  // pronta do backend — o câmbio digitado aqui NÃO altera o saldo, só projeta
  // quanto isso daria em reais. As demais categorias são em BRL.
  const isForeign = (cat) => FOREIGN_CATS.includes(cat) && rowOf(cat)?.balance_foreign != null;
  // Num pagamento em USD/RMB, categorias cobradas em reais (impostos,
  // despachante, armazenagem, taxas) são digitadas em R$: a dívida delas é em
  // reais, e convertê-las por um câmbio que ainda está sendo calculado distorce.
  const localCat = (cat) => !brlNative && !FOREIGN_CATS.includes(cat);

  // Câmbio "normal" (R$ por unidade da moeda) — referência para o alerta de
  // digitação e para pagamentos em moeda só de categorias em reais
  const refRate = (cur, r = rmbRate) => {
    const usd = balance?.exchange_rate || 0;
    return cur === 'RMB' ? usd / r : usd;
  };

  const selected = CATS.filter(c => allocs[c].on);
  const amt = (c) => parseBRL(allocs[c].amount);
  const orderAmt = (c) => parseBRL(allocs[c].order);
  const fxSum = selected.filter(c => !localCat(c)).reduce((s, c) => s + amt(c), 0);   // moeda do pagamento
  const localBrl = selected.filter(localCat).reduce((s, c) => s + amt(c), 0);        // R$
  // Pagamento em USD/RMB: dá para digitar o câmbio OU o valor debitado em reais.
  // O último campo editado manda (`rate_mode`). No modo 'brl':
  //   câmbio = (R$ pago − R$ das categorias em reais) ÷ total na moeda do pagamento.
  // Sem valor na moeda (só categorias "final" ou só em reais) cai no câmbio
  // digitado; se só há categorias em reais, o câmbio nem importa (o R$ de cada
  // uma é gravado exato) e vale o câmbio do pedido.
  const brlMode = !brlNative && meta.rate_mode === 'brl';
  const brlTyped = parseBRL(meta.brl_total);
  const typedRate = parseRate(meta.exchange_rate);
  const rateFromBrl = brlMode && fxSum > 0;
  const onlyLocal = fxSum === 0 && localBrl > 0;
  const effRate = brlNative ? 1
    : rateFromBrl ? Math.max(0, brlTyped - localBrl) / fxSum
    : typedRate > 0 ? typedRate
    : onlyLocal ? refRate(meta.currency, payRmbRate)
    : 0;
  const totalBrl = rateFromBrl ? brlTyped : fxSum * effRate + localBrl;
  const totalForeign = fxSum + (effRate > 0 ? localBrl / effRate : 0);

  // Converte entre a moeda do pedido e a do pagamento (USD <-> RMB)
  // `r` = taxa RMB/USD da moeda envolvida (na troca de moeda, cada lado usa a sua)
  const orderToCur = (v, cur, r = rmbFor(cur)) => {
    if (cur === 'RMB' && orderCur === 'USD') return v * r;
    if (cur === 'USD' && orderCur === 'RMB') return v / r;
    return v;
  };
  const curToOrder = (v, cur, r = rmbFor(cur)) => {
    if (cur === 'RMB' && orderCur === 'USD') return v / r;
    if (cur === 'USD' && orderCur === 'RMB') return v * r;
    return v;
  };
  const saldoNaMoeda = (cat) => {
    if (!isForeign(cat)) return { valor: saldoOf(cat), moeda: 'BRL' };
    return { valor: rowOf(cat).balance_foreign, moeda: orderCur };
  };
  // Projeção em reais do saldo estrangeiro: câmbio do formulário, senão o travado
  const saldoBrlPrevisto = (cat) => {
    if (!isForeign(cat)) return null;
    const bf = rowOf(cat).balance_foreign;
    const usd = orderCur === 'RMB' ? bf / rmbRate : bf;
    const fx = (!brlNative && effRate > 0)
      ? (meta.currency === 'RMB' ? effRate * payRmbRate : effRate)
      : (balance?.exchange_rate || 0);
    return usd * fx;
  };

  function sm(k, v) { setMeta(m => ({ ...m, [k]: v })); }

  // valor da categoria pré-preenchido com o saldo devido
  function prefillFor(cat) {
    if (!brlNative && FOREIGN_CATS.includes(cat)) {
      if (!isForeign(cat)) return '';
      const v = Math.max(0, orderToCur(rowOf(cat).balance_foreign, meta.currency));
      return v > 0 ? v.toFixed(2) : '';
    }
    // pagamento em BRL, ou categoria cobrada em reais: o saldo já é em R$
    const s = Math.max(0, saldoOf(cat));
    return s > 0 ? s.toFixed(2) : '';
  }

  // Fornecedor/frete pagos em reais: informa-se o BRL desembolsado E quanto
  // ele quitou na moeda do pedido; o câmbio do pagamento é a razão entre os dois.
  const needsEquiv = (cat) => brlNative && isForeign(cat);
  const impliedRate = (cat) => {
    const brl = amt(cat);
    const ord = orderAmt(cat);
    return brl > 0 && ord > 0 ? brl / ord : null;
  };

  function toggleCat(cat) {
    setAllocs(a => {
      const on = !a[cat].on;
      if (on && needsEquiv(cat)) {
        // pré-preenche só o restante em USD; o BRL desembolsado é digitado
        const bf = Math.max(0, rowOf(cat).balance_foreign);
        return { ...a, [cat]: { ...a[cat], on, order: a[cat].order || (bf > 0 ? bf.toFixed(2) : '') } };
      }
      return { ...a, [cat]: { ...a[cat], on, amount: on && !a[cat].amount ? prefillFor(cat) : a[cat].amount } };
    });
  }
  function setAllocField(cat, k, v) {
    setAllocs(a => ({ ...a, [cat]: { ...a[cat], [k]: v } }));
  }

  // Trocar a moeda preserva o que já foi digitado:
  //  · para BRL: fornecedor/frete ganham o R$ (valor × câmbio) e o valor na
  //    moeda vira o "quitado";
  //  · de BRL: o "quitado" vira o valor na moeda e o R$ já digitado vai para
  //    "Valor pago em R$" (o câmbio passa a ser calculado dele);
  //  · entre USD e RMB: valores e câmbio digitado são convertidos pela taxa RMB.
  // Linhas desmarcadas de fornecedor/frete são limpas (voltam pré-preenchidas).
  // Categorias em reais não mudam — são R$ em qualquer moeda.
  function changeCurrency(next) {
    const prev = meta.currency;
    if (next === prev) return;
    fxReq.current++;
    const fmt = (v) => (v > 0 ? v.toFixed(2) : '');
    const toNext = (v) => orderToCur(curToOrder(v, prev), next);   // USD <-> RMB
    // R$ já digitado num pagamento em BRL: das categorias em reais e das linhas
    // de fornecedor/frete com "quitado" (que viram o valor na moeda). Linha sem
    // quitado fica vazia e não pode empurrar o seu R$ para o câmbio das outras.
    const keeps = (c) => !FOREIGN_CATS.includes(c) || parseBRL(allocs[c].order) > 0;
    const carryBrl = prev === 'BRL' && FOREIGN_CATS.some(c => allocs[c].on && amt(c) > 0 && keeps(c))
      ? selected.filter(keeps).reduce((s, c) => s + amt(c), 0) : 0;

    setAllocs(a => {
      const out = { ...a };
      for (const c of FOREIGN_CATS) {
        const r = a[c];
        if (!r.on) { out[c] = { ...r, amount: '', order: '' }; continue; }
        const v = parseBRL(r.amount);
        if (next === 'BRL') out[c] = { ...r, amount: effRate > 0 ? fmt(v * effRate) : '', order: fmt(curToOrder(v, prev)) || r.order };
        else if (prev === 'BRL') out[c] = { ...r, amount: fmt(orderToCur(parseBRL(r.order), next)), order: '' };
        else out[c] = { ...r, amount: fmt(toNext(v)) };
      }
      return out;
    });

    setMeta(m => {
      let rate = '';
      if (next !== 'BRL' && prev !== 'BRL' && typedRate > 0) {
        // mesmo R$ por unidade de valor: R$/USD ÷ RMB por USD = R$/RMB (e vice-versa)
        rate = String(+(typedRate / toNext(1)).toFixed(6));
      }
      if (carryBrl > 0) return { ...m, currency: next, exchange_rate: '', brl_total: fmtBRLInput(carryBrl), rate_mode: 'brl' };
      if (next === 'BRL') return { ...m, currency: next, exchange_rate: '', brl_total: '', rate_mode: 'rate' };
      return { ...m, currency: next, exchange_rate: rate };
    });
  }

  async function loadFx() {
    const my = ++fxReq.current;
    const cur = meta.currency;
    const brlAtClick = meta.brl_total;
    const rateAtClick = meta.exchange_rate;
    try {
      const fx = await fxApi.get();
      if (my !== fxReq.current) return;                     // o formulário mudou (outra busca, edição, moeda)
      let r = fx.rate_with_buffer;
      if (cur === 'RMB') {
        // BRL por RMB = BRL por USD × USD por RMB
        if (!(fx.rmb_usd > 0)) return toast('Câmbio do RMB indisponível agora — digite o câmbio ou o valor em R$', 'error');
        r = r * fx.rmb_usd;
      }
      setMeta(m => {
        if (m.currency !== cur) return m;                   // trocou de moeda enquanto buscava
        if (m.exchange_rate !== rateAtClick) return m;      // digitou um câmbio enquanto buscava: o digitado manda
        if (m.brl_total !== brlAtClick) return { ...m, exchange_rate: r.toFixed(4) }; // digitou R$: o R$ manda
        return { ...m, exchange_rate: r.toFixed(4), rate_mode: 'rate' };
      });
    } catch { toast('Não foi possível buscar câmbio', 'error'); }
  }

  async function handleSave() {
    if (selected.length === 0) return toast('Selecione ao menos uma categoria', 'error');
    const bad = selected.find(c => !allocs[c].final && !(amt(c) > 0));
    if (bad) return toast(`Informe o valor de "${CAT_LABELS[bad]}" ou marque como final`, 'error');
    const semEquiv = selected.find(c => needsEquiv(c) && amt(c) > 0 && !(orderAmt(c) > 0));
    if (semEquiv) return toast(`Informe quanto "${CAT_LABELS[semEquiv]}" quitou em ${orderCur}`, 'error');
    if (!brlNative) {
      if (rateFromBrl && !(brlTyped > 0)) return toast('Informe o valor pago em R$ ou o câmbio', 'error');
      if (rateFromBrl && !(brlTyped > localBrl)) return toast('O valor pago em R$ não cobre as categorias em reais deste pagamento', 'error');
      if (brlMode && !rateFromBrl && brlTyped > 0) {
        if (localBrl === 0) return toast(`Informe os valores em ${meta.currency} para calcular o câmbio`, 'error');
        // Só categorias em reais: o R$ digitado tem de bater com a soma delas —
        // senão parte do dinheiro foi para fornecedor/frete sem valor na moeda
        if (Math.round(brlTyped * 100) !== Math.round(localBrl * 100)) {
          return toast(`O valor pago (${fmtBRL(brlTyped)}) não bate com as categorias em reais (${fmtBRL(localBrl)}). Se parte foi para fornecedor/frete, informe o valor em ${meta.currency}.`, 'error');
        }
      }
      if (!(effRate > 0)) return toast('Preencha o câmbio ou o valor pago em R$', 'error');
    }

    // Alerta de digitação: câmbio muito longe do câmbio do pedido costuma ser
    // um valor em R$ ou em USD digitado errado (ex.: "52.345" lido como 52,35).
    const suspeitos = [];
    const ref = refRate(meta.currency, payRmbRate);
    if (!brlNative && ref > 0 && fxSum > 0 && (effRate < ref * 0.7 || effRate > ref * 1.3)) {
      suspeitos.push(`câmbio R$ ${effRate.toFixed(4)} por ${meta.currency} (pedido: R$ ${ref.toFixed(4)})`);
    }
    if (brlNative) {
      const refO = refRate(orderCur);
      selected.filter(needsEquiv).forEach(c => {
        const r = impliedRate(c);
        if (r && refO > 0 && (r < refO * 0.7 || r > refO * 1.3)) suspeitos.push(`${CAT_LABELS[c]}: câmbio R$ ${r.toFixed(4)} por ${orderCur} (pedido: R$ ${refO.toFixed(4)})`);
      });
    }
    if (suspeitos.length && !window.confirm(`Confira os valores — o câmbio ficou muito diferente do câmbio do pedido:\n\n${suspeitos.join('\n')}\n\nSalvar mesmo assim?`)) return;

    const allocations = selected.map(c => ({
      category: c,
      // categorias em reais num pagamento em moeda: R$ ÷ câmbio → o backend
      // grava amount_brl = valor × câmbio = exatamente o R$ digitado
      amount_foreign: localCat(c) ? amt(c) / effRate : amt(c),
      ...(needsEquiv(c) && orderAmt(c) > 0 ? { amount_order: orderAmt(c) } : {}),
      is_final: !!allocs[c].final,
    }));

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('currency', meta.currency || 'BRL');
      fd.append('exchange_rate', String(effRate));
      fd.append('entry_mode', rateFromBrl ? 'brl' : 'rate');
      fd.append('payment_date', meta.payment_date);
      fd.append('notes', meta.notes);
      if (meta.payee) fd.append('payee', meta.payee);
      fd.append('allocations', JSON.stringify(allocations));
      if (receipt) fd.append('receipt', receipt);
      if (editing) {
        if (removeReceipt && !receipt) fd.append('remove_receipt', '1');
        await ordersApi.updatePayment(editing.id, fd);
      } else {
        await ordersApi.addPayment(q.id, fd);
      }
      toast(editing ? 'Pagamento atualizado!' : 'Pagamento registrado!', 'success');
      onSaved();
    } catch (e) { toast(e.response?.data?.error || 'Erro', 'error'); }
    finally { setSaving(false); }
  }

  // Fechou o formulário com uma busca de câmbio pendente: a resposta é ignorada
  useEffect(() => () => { fxReq.current++; }, []);

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{editing ? '✏️ Editar Pagamento' : '💳 Registrar Pagamento'}</div>
          <button className="btn btn-ghost btn-sm btn-icon" onClick={() => onClose()}>✕</button>
        </div>
        <div className="modal-body">
          {/* Moeda + data ; câmbio + R$ (lado a lado, um calcula o outro) ; beneficiário */}
          <div className="form-grid">
            <div className="form-group">
              <label>Moeda</label>
              <select value={meta.currency} onChange={e => changeCurrency(e.target.value)}>
                <option value="BRL">BRL</option>
                <option value="USD">USD</option>
                <option value="RMB">RMB</option>
              </select>
            </div>
            <div className="form-group">
              <label>Data do Pagamento</label>
              <input type="date" value={meta.payment_date} onChange={e => sm('payment_date', e.target.value)} />
            </div>
            <div className="form-group">
              <label>
                Câmbio → BRL {brlNative && <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>(BRL = 1)</span>}
                {rateFromBrl && <span style={{ fontSize: 11, color: 'var(--gray-400)' }}> (calculado)</span>}
              </label>
              <input type="text" inputMode="decimal"
                value={brlNative ? '1' : rateFromBrl ? (effRate > 0 ? effRate.toFixed(4) : '') : meta.exchange_rate}
                onChange={e => setMeta(m => ({ ...m, exchange_rate: e.target.value, rate_mode: 'rate' }))}
                onFocus={e => e.target.select()}
                placeholder="5.2000" disabled={brlNative} />
              {!brlNative && <span className="form-hint" style={{ color: 'var(--blue)', cursor: 'pointer' }} onClick={loadFx}>🔄 Buscar câmbio atual</span>}
            </div>
            {brlNative ? <div className="form-group" /> : (
              <div className="form-group">
                <label>Valor pago em R$ {!brlMode && effRate > 0 && totalBrl > 0 && <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>(calculado)</span>}</label>
                <input type="text" inputMode="decimal"
                  // Calculado só quando há câmbio: sem câmbio o campo fica vazio para digitar
                  value={brlMode ? meta.brl_total : (effRate > 0 && totalBrl > 0 ? fmtBRLInput(totalBrl) : '')}
                  onFocus={e => e.target.select()}
                  onChange={e => setMeta(m => ({ ...m, brl_total: e.target.value, rate_mode: 'brl' }))}
                  placeholder="R$ debitado no banco" />
                <span className="form-hint" style={{ color: 'var(--gray-400)' }}>
                  {brlMode && brlTyped > 0
                    ? <>Entendido: <strong>{fmtBRL(brlTyped)}</strong> — {rateFromBrl
                        ? 'o câmbio é calculado'
                        : onlyLocal ? 'só categorias em reais' : `informe o valor em ${meta.currency} do fornecedor/frete`}</>
                    : <>Digite o R$ que saiu da conta — o câmbio é calculado (R$ ÷ {meta.currency})</>}
                </span>
              </div>
            )}
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label>Beneficiário (opcional)</label>
              <input value={meta.payee} onChange={e => sm('payee', e.target.value)} placeholder="Ex: Despachante XYZ, Receita Federal" />
            </div>
          </div>

          {/* Categorias cobertas por este pagamento */}
          <div style={{ marginTop: 8, marginBottom: 6, fontSize: 12, fontWeight: 700, color: 'var(--pantone)' }}>
            Categorias deste pagamento
          </div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="table" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ width: 30 }}></th>
                  <th>Categoria</th>
                  <th style={{ textAlign: 'right' }}>Saldo</th>
                  <th style={{ textAlign: 'right', width: 150 }}>Valor</th>
                  <th style={{ textAlign: 'center', width: 70 }}>Final</th>
                </tr>
              </thead>
              <tbody>
                {CATS.map(c => {
                  const on = allocs[c].on;
                  const saldo = saldoNaMoeda(c);
                  return (
                    <tr key={c} style={{ background: on ? 'var(--gray-50)' : 'transparent' }}>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={on} onChange={() => toggleCat(c)} />
                      </td>
                      <td>{CAT_LABELS[c]}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: saldo.valor > 0.01 ? '#C53030' : '#276749' }}>
                        {fmtIn(saldo.moeda, saldo.valor)}
                        {saldo.moeda !== 'BRL' && saldo.valor > 0.01 && (
                          <div style={{ fontSize: 10, color: 'var(--gray-400)' }}>≈ {fmtBRL(saldoBrlPrevisto(c))} previsto</div>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {on
                          ? <>
                              {/* moeda deste valor: fornecedor/frete na moeda do pagamento; o resto em R$ */}
                              <span style={{ fontSize: 10, color: 'var(--gray-400)', marginRight: 4 }}>{brlNative || localCat(c) ? 'R$' : meta.currency}</span>
                              <input type="text" inputMode="decimal" value={allocs[c].amount} onChange={e => setAllocField(c, 'amount', e.target.value)} placeholder={needsEquiv(c) ? 'R$ pago' : '0.00'} title={needsEquiv(c) ? 'Valor desembolsado em reais' : localCat(c) ? 'Categoria cobrada em reais — valor em R$' : undefined} style={{ width: 120, textAlign: 'right' }} />
                              {needsEquiv(c) && (
                                <div style={{ marginTop: 4 }}>
                                  <span style={{ fontSize: 10, color: 'var(--gray-400)', marginRight: 4 }}>{orderCur}</span>
                                  <input type="text" inputMode="decimal" value={allocs[c].order} onChange={e => setAllocField(c, 'order', e.target.value)} placeholder={`${orderCur} quitado`} title={`Quanto este pagamento quitou em ${orderCur}`} style={{ width: 120, textAlign: 'right' }} />
                                  <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 2 }}>
                                    {impliedRate(c) ? `câmbio R$ ${impliedRate(c).toFixed(4)}` : `R$ pago + ${orderCur} quitado`}
                                  </div>
                                </div>
                              )}
                            </>
                          : <span style={{ color: 'var(--gray-300)' }}>—</span>}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {on && <input type="checkbox" checked={allocs[c].final} onChange={e => setAllocField(c, 'final', e.target.checked)} title="Pagamento final — zera o saldo desta categoria" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Total + comprovante + obs */}
          <div className="form-grid" style={{ marginTop: 12 }}>
            <div className="form-group">
              <label>Total do pagamento</label>
              <div style={{ padding: '9px 12px', background: 'var(--gray-50)', borderRadius: 'var(--radius)', border: '1px solid var(--gray-200)', fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--pantone)' }}>
                {meta.currency} {totalForeign.toFixed(2)} {!brlNative && <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>= {fmtBRL(totalBrl)}</span>}
              </div>
            </div>
            <div className="form-group">
              <label>Comprovante (PDF ou imagem)</label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => { setReceipt(e.target.files[0] || null); setRemoveReceipt(false); }} />
              {editing && editing.receipt_filename && !receipt && (
                <div style={{ fontSize: 11, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {removeReceipt
                    ? <span style={{ color: '#C53030' }}>Comprovante será removido ao salvar</span>
                    : <a href={`/uploads/${editing.receipt_filename}`} target="_blank" rel="noreferrer" style={{ color: 'var(--blue)' }}>
                        📎 {editing.receipt_original_name || 'Comprovante atual'}
                      </a>}
                  <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '1px 8px', fontSize: 11 }}
                    onClick={() => setRemoveReceipt(v => !v)}>
                    {removeReceipt ? 'Manter' : 'Remover'}
                  </button>
                </div>
              )}
            </div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label>Observações</label>
              <input value={meta.notes} onChange={e => sm('notes', e.target.value)} placeholder="Ex: repasse do despachante — fechamento da DI" />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={() => onClose()}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Salvando...' : (editing ? 'Salvar alterações' : 'Registrar')}</button>
        </div>
      </div>
    </div>
  );
}
